import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    return `/dashboard/${user.role}`;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-effect shadow-lg py-4' : 'bg-transparent py-6'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="text-2xl font-bold tracking-tight">
              <span className="text-[#D4AF37]">NEHPETS</span>
              <span className="text-white">+</span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link
              to="/services"
              className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              data-testid="nav-services"
            >
              Services
            </Link>
            <Link
              to="/gallery"
              className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              data-testid="nav-gallery"
            >
              Gallery
            </Link>
            <Link
              to="/about"
              className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              data-testid="nav-about"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Link to={getDashboardLink()}>
                  <Button
                    className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-6 py-3 transition-all"
                    data-testid="dashboard-button"
                  >
                    Dashboard
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  className="bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-6 py-3 transition-all"
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    className="bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-6 py-3 transition-all"
                    data-testid="login-button"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    className="bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-6 py-3 transition-all"
                    data-testid="contact-nav-button"
                  >
                    Contact Us
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden mt-6 pb-4 space-y-4" data-testid="mobile-menu">
            <Link
              to="/"
              className="block text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/services"
              className="block text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              Services
            </Link>
            <Link
              to="/gallery"
              className="block text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              Gallery
            </Link>
            <Link
              to="/about"
              className="block text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              About
            </Link>
            <Link
              to="/contact"
              className="block text-white/70 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-wider"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </Link>
            {user ? (
              <>
                <Link to={getDashboardLink()} onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-6 py-3">
                    Dashboard
                  </Button>
                </Link>
                <Button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-6 py-3"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black uppercase tracking-widest font-bold text-xs px-6 py-3">
                    Login
                  </Button>
                </Link>
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-6 py-3">
                    Contact Us
                  </Button>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;